import { Ellipsis, Send, Trash2, User } from 'lucide-react';

import { AVATAR_FALLBACK_TONE, AVATAR_SIZE } from '@/components/ui/types/get-avatar-initials-enums';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CONTROL_SIZE } from '@/components/ui/types/control-enums';
import { DROPDOWN_MENU_DENSITY, DropdownMenuItemVariant } from '@/components/ui/types/dropdown-menu-variants-enums';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ICON_BUTTON_SIZE, ICON_BUTTON_VARIANT } from '@/components/ui/types/icon-button-variants-enums';
import { IconButton } from '@/components/ui/icon-button';
import { SELECT_APPEARANCE } from '@/components/ui/types/select-variants-enums';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SettingsRow } from '@/components/ui/settings-list';
import { STATUS_BADGE_APPEARANCE, STATUS_BADGE_SIZE, STATUS_TONE } from '@/components/ui/types/status-badge-variants-enums';
import { StatusBadge } from '@/components/ui/status-badge';
import { TABLE_CELL_KIND } from '@/components/ui/types/table-variants-enums';
import { TableCell, TableRow } from '@/components/ui/table';

import { capitalizeRole } from '../helpers/team-member-helpers';

import type { TeamMember } from '../types/team-types';
import { MemberRole } from '@/services/internal-api/accounts/types/accounts-api-service-enums';

import { TEAM_MEMBER_STATUS } from '../types/team-enums';

const STATUS_TEXT: Record<TeamMember['status'], string> = {
  [TEAM_MEMBER_STATUS.ACTIVE]: 'Active',
  [TEAM_MEMBER_STATUS.PENDING]: 'Pending',
};

function getStatusTone(status: TeamMember['status']): STATUS_TONE {
  return status === TEAM_MEMBER_STATUS.ACTIVE ? STATUS_TONE.SUCCESS : STATUS_TONE.NEUTRAL;
}

function MemberStatus({ status }: { status: TeamMember['status'] }) {
  return (
    <StatusBadge
      appearance={STATUS_BADGE_APPEARANCE.DOT}
      label={STATUS_TEXT[status]}
      size={STATUS_BADGE_SIZE.COMPACT}
      tone={getStatusTone(status)}
    />
  );
}

function shouldShowEmailOnly(member: TeamMember): boolean {
  const normalizedName = member.name.trim();
  return (
    normalizedName.length === 0 ||
    normalizedName.localeCompare(member.email, undefined, { sensitivity: 'accent' }) === 0 ||
    (member.status === TEAM_MEMBER_STATUS.PENDING && normalizedName.toLowerCase() === 'invited user')
  );
}

function MemberAvatar({ member }: { member: TeamMember }) {
  const trimmedName = member.name.trim();

  return (
    <Avatar size={AVATAR_SIZE.SM}>
      <AvatarImage src={member.avatar} alt="" />
      <AvatarFallback
        name={trimmedName || undefined}
        tone={AVATAR_FALLBACK_TONE.NEUTRAL}
      >
        {trimmedName ? undefined : <User className="size-4" aria-hidden="true" />}
      </AvatarFallback>
    </Avatar>
  );
}

function MemberIdentity({ member }: { member: TeamMember }) {
  const emailOnly = shouldShowEmailOnly(member);
  const primaryText = emailOnly ? member.email : member.name;

  return (
    <div className="min-w-0 flex-1">
      <div className="flex min-w-0 items-center gap-1.5">
        <span
          className="min-w-0 truncate text-sm/5 font-medium text-content-primary"
          title={primaryText}
        >
          {primaryText}
        </span>
        {member.isCurrentUser ? (
          <span className="shrink-0 text-xs/4 font-normal text-content-tertiary">You</span>
        ) : null}
      </div>
      {!emailOnly ? (
        <div className="truncate text-xs/4 font-normal text-content-secondary" title={member.email}>
          {member.email}
        </div>
      ) : null}
    </div>
  );
}

interface TeamMemberRowProps {
  member: TeamMember;
  availableRoles: TeamMember['role'][];
  currentUserRole: TeamMember['role'] | null;
  onRoleChange: (memberId: string | number, newRole: TeamMember['role']) => void;
  onResendInvite: (memberId: string | number) => void;
  onCancelInvite: (memberId: string | number) => void;
  onDeleteMember: (memberId: string | number) => void;
}

function isProtectedOwner(member: TeamMember, currentUserRole: TeamMember['role'] | null): boolean {
  return currentUserRole === MemberRole.ADMIN && member.role === MemberRole.OWNER;
}

function MemberRoleControl({
  member,
  availableRoles,
  currentUserRole,
  onRoleChange,
}: {
  member: TeamMember;
  availableRoles: TeamMember['role'][];
  currentUserRole: TeamMember['role'] | null;
  onRoleChange: (memberId: string | number, newRole: TeamMember['role']) => void;
}) {
  const isReadOnly = member.isCurrentUser || isProtectedOwner(member, currentUserRole);

  if (isReadOnly) {
    return (
      <span
        aria-label={`Role for ${member.email}`}
        className="type-field-label font-medium text-content-primary"
        title={capitalizeRole(member.role)}
      >
        {capitalizeRole(member.role)}
      </span>
    );
  }

  return (
    <Select
      appearance={SELECT_APPEARANCE.DEFAULT}
      size={CONTROL_SIZE.COMPACT}
      value={member.role}
      onValueChange={(newRole: TeamMember['role']) => onRoleChange(member.id, newRole)}
    >
      <SelectTrigger
        className="w-[120px]"
        aria-label={`Role for ${member.email}`}
      >
        <SelectValue placeholder="Select role" />
      </SelectTrigger>
      <SelectContent>
        {availableRoles.map(role => (
          <SelectItem key={role} value={role}>
            {capitalizeRole(role)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function TeamMemberRow({
  member,
  availableRoles,
  currentUserRole,
  onRoleChange,
  onResendInvite,
  onCancelInvite,
  onDeleteMember,
}: TeamMemberRowProps) {
  const hasActions = !member.isCurrentUser && !isProtectedOwner(member, currentUserRole);

  return (
    <TableRow data-slot="team-member-row">
      <TableCell>
        <div className="flex min-w-0 items-center gap-3">
          <MemberAvatar member={member} />
          <MemberIdentity member={member} />
        </div>
      </TableCell>
      <TableCell kind={TABLE_CELL_KIND.STATUS}>
        <MemberStatus status={member.status} />
      </TableCell>
      <TableCell>
        <MemberRoleControl
          member={member}
          availableRoles={availableRoles}
          currentUserRole={currentUserRole}
          onRoleChange={onRoleChange}
        />
      </TableCell>
      <TableCell kind={TABLE_CELL_KIND.ACTION}>
        {hasActions ? (
          <MemberActions
            member={member}
            onResendInvite={onResendInvite}
            onCancelInvite={onCancelInvite}
            onDeleteMember={onDeleteMember}
          />
        ) : null}
      </TableCell>
    </TableRow>
  );
}

export function TeamMemberMobileRow({
  member,
  availableRoles,
  currentUserRole,
  onRoleChange,
  onResendInvite,
  onCancelInvite,
  onDeleteMember,
}: TeamMemberRowProps) {
  const hasActions = !member.isCurrentUser && !isProtectedOwner(member, currentUserRole);

  return (
    <SettingsRow
      className="items-start"
      data-slot="team-member-row"
      leading={<MemberAvatar member={member} />}
      title={<MemberIdentity member={member} />}
      description={
        <span className="flex flex-wrap items-center gap-x-3 gap-y-2 pt-1">
          <MemberStatus status={member.status} />
          <MemberRoleControl
            member={member}
            availableRoles={availableRoles}
            currentUserRole={currentUserRole}
            onRoleChange={onRoleChange}
          />
        </span>
      }
      trailing={
        hasActions ? (
          <MemberActions
            member={member}
            onResendInvite={onResendInvite}
            onCancelInvite={onCancelInvite}
            onDeleteMember={onDeleteMember}
          />
        ) : null
      }
    />
  );
}

function MemberActions({
  member,
  onResendInvite,
  onCancelInvite,
  onDeleteMember,
}: {
  member: TeamMember;
  onResendInvite: (memberId: string | number) => void;
  onCancelInvite: (memberId: string | number) => void;
  onDeleteMember: (memberId: string | number) => void;
}) {
  return (
    <DropdownMenu density={DROPDOWN_MENU_DENSITY.COMPACT}>
      <DropdownMenuTrigger asChild>
        <IconButton
          label={`Actions for ${member.email}`}
          size={ICON_BUTTON_SIZE.COMPACT}
          variant={ICON_BUTTON_VARIANT.GHOST}
        >
          <Ellipsis aria-hidden="true" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {member.status === TEAM_MEMBER_STATUS.PENDING ? (
          <>
            <DropdownMenuItem onSelect={() => onResendInvite(member.id)}>
              <Send aria-hidden="true" />
              Resend invitation
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant={DropdownMenuItemVariant.DESTRUCTIVE}
              onSelect={() => onCancelInvite(member.id)}
            >
              <Trash2 aria-hidden="true" />
              Cancel invitation
            </DropdownMenuItem>
          </>
        ) : (
          <DropdownMenuItem
            variant={DropdownMenuItemVariant.DESTRUCTIVE}
            onSelect={() => onDeleteMember(member.id)}
          >
            <Trash2 aria-hidden="true" />
            Remove member
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
